'use client'

import { useEffect, useState } from 'react'

import throttle from 'lodash/throttle'

export const WINDOW_DIMENSION_TYPE = {
  mobile: 'mobile',
  sm: 'sm',
  md: 'md',
  lg: 'lg,',
  laptop: 'laptop',
  desktop: 'desktop',
  wide: 'wide',
} as const

export type WindowDimensionType = (typeof WINDOW_DIMENSION_TYPE)[keyof typeof WINDOW_DIMENSION_TYPE]

interface WindowDimensionInfo {
  limit: number
  type: WindowDimensionType
}

// based on sizes set in variables.scss file
export const WINDOW_DIMENSIONS: WindowDimensionInfo[] = [
  { limit: 767, type: WINDOW_DIMENSION_TYPE.sm },
  { limit: 991, type: WINDOW_DIMENSION_TYPE.md },
  { limit: 1199, type: WINDOW_DIMENSION_TYPE.lg },
  { limit: 1399, type: WINDOW_DIMENSION_TYPE.laptop },
  { limit: 1400, type: WINDOW_DIMENSION_TYPE.desktop },
  { limit: 1640, type: WINDOW_DIMENSION_TYPE.wide },
]

interface WindowDimensions {
  width: number | null
  height: number | null
  /** True once component has mounted on client and dimensions are accurate */
  hasMounted: boolean
}

export const useWindowDimensions = (_shouldReset?: boolean): WindowDimensions => {
  // Use null for SSR to avoid hydration mismatch
  // Components should handle null by showing a consistent initial state
  const [windowDimensions, setWindowDimensions] = useState<WindowDimensions>({
    width: null,
    height: null,
    hasMounted: false,
  })

  useEffect(() => {
    // Set initial dimensions immediately after mount (client-side only)
    setWindowDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
      hasMounted: true,
    })

    const throttledHandler = throttle(() => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
        hasMounted: true,
      })
    }, 300)

    window.addEventListener('resize', throttledHandler)

    return () => window.removeEventListener('resize', throttledHandler)
  }, []) // Empty array ensures that effect is only run on mount

  return windowDimensions
}
